import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  UserDto,
  ResponseDtoUserDto,
  ResponseDtoPageUserDto,
  ResponseDtoUserDetailsMetadataDto,
  UserDetailsRequestDto,
  UserPermissionDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

// Interface for user filters
export interface UserFilters {
  names?: string[];
  emails?: string[];
  billedEntities?: string[];
  shippers?: string[];
  consignees?: string[];
  shippingProviders?: string[];
  shippingServices?: string[];
  alloweds?: string[];
  permissions?: string[];
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// Interface for pagination and search parameters
interface UserListParams {
  page?: number;
  size?: number;
  sortingColum?: string;
  sortingDirection?: 'ASC' | 'DESC';
}

/**
 * Hook to fetch a paginated list of users
 * @param params Pagination and sorting parameters
 * @returns Query result with paginated users data
 */
export const useUserList = (params: UserListParams, filters?: UserFilters): UseQueryResult<ResponseDtoPageUserDto> =>
  useQuery(
    ['users', params, filters],
    async () => {
      // Build request data with filters and pagination
      const requestDto: UserDetailsRequestDto = {
        paginationRequest: {
          page: params.page,
          size: params.size,
          sortingColum: params.sortingColum,
          sortingDirection: params.sortingDirection,
        },
      };

      // Add filters if they exist
      if (filters) {
        if (filters.names?.length) requestDto.firstNames = filters.names;
        if (filters.emails?.length) requestDto.emails = filters.emails;
        // Map billedEntities to legalEntityCodes as per API requirements
        if (filters.billedEntities?.length) requestDto.legalEntityCodes = filters.billedEntities;
        // Other filters from the UserFilters interface are not supported by UserDetailsRequestDto
        // and will be ignored
      }

      const response = await tFinancialService.api.queryUsers(requestDto);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

/**
 * Hook to create or update users
 * @returns Mutation function for upserting users
 */
export const useUpsertUser = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (user: UserDto): Promise<ResponseDtoUserDto> => {
      const response = await tFinancialService.api.upsertUser(user);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['users']);
      },
    }
  );
};

/**
 * Hook to fetch users metadata for filtering options
 * This provides filter options for names, emails, etc.
 */
export const useUsersMetadata = (filters: UserFilters = {}): UseQueryResult<ResponseDtoUserDetailsMetadataDto> =>
  useQuery(
    ['usersMetadata', filters],
    async (): Promise<ResponseDtoUserDetailsMetadataDto> => {
      // Build request data with filters
      const requestDto: UserDetailsRequestDto = {
        // Default empty pagination request as it's required by the API
        paginationRequest: {},
      };

      // Add pagination if provided
      if (filters.page !== undefined || filters.size !== undefined || filters.sortBy || filters.sortDirection) {
        const paginationRequest: any = {};

        if (filters.page !== undefined) paginationRequest.page = filters.page;
        if (filters.size !== undefined) paginationRequest.size = filters.size;
        if (filters.sortBy) paginationRequest.sortingColum = filters.sortBy;
        if (filters.sortDirection) paginationRequest.sortingDirection = filters.sortDirection;

        requestDto.paginationRequest = paginationRequest;
      }

      // Only add filter arrays if they have values
      if (filters.names?.length) requestDto.firstNames = filters.names;
      if (filters.emails?.length) requestDto.emails = filters.emails;
      // Map billedEntities to legalEntityCodes as per API requirements
      if (filters.billedEntities?.length) requestDto.legalEntityCodes = filters.billedEntities;
      // Other filters from the UserFilters interface are not supported by UserDetailsRequestDto
      // and will be ignored

      return (await tFinancialService.api.getUsersMetadata(requestDto)).data;
    },
    {
      // Keep the data fresh but don't refetch on window focus
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

/**
 * Hook to fetch a single user's details
 * @param userId User ID to fetch details for
 * @returns Query result with user data
 */
export const useUserDetail = (userId?: string): UseQueryResult<ResponseDtoUserDto> =>
  useQuery(
    ['user', userId],
    async () => {
      if (!userId) throw new Error('User ID is required');

      const users = await tFinancialService.api.queryUsers({
        paginationRequest: { page: 0, size: 1 },
      });

      const user = users.data?.data?.content?.find((u) => u.id === userId);
      if (!user) throw new Error('User not found');

      return { data: user } as ResponseDtoUserDto;
    },
    {
      enabled: !!userId,
    }
  );

/**
 * Hook to create or update user permissions
 * Uses PUT endpoint which handles both create and update operations (upsert)
 * @returns Mutation function for creating/updating user permissions
 */
export const useUpsertUserPermission = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (permission: UserPermissionDto) => {
      // Use the upsert endpoint which handles both create and update
      const response = await tFinancialService.api.upsertUserPermission(permission);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Use a longer delay to ensure the table has completely finished its state transitions
        // This prevents the cache invalidation from interfering with the table's edit state
        setTimeout(() => {
          // Invalidate all users-related queries to refresh user list with updated permissions
          queryClient.invalidateQueries({ queryKey: ['users'] });

          // Invalidate the specific user detail query to refresh the user's permission list
          if (variables.userId) {
            queryClient.invalidateQueries({ queryKey: ['user', variables.userId] });
          }

          // Also invalidate users metadata to update filter options
          queryClient.invalidateQueries({ queryKey: ['usersMetadata'] });

          // Invalidate user details and permission queries in case the current user's permissions were updated
          queryClient.invalidateQueries({ queryKey: cacheKeysUtil.userDetails() });
          queryClient.invalidateQueries({ queryKey: cacheKeysUtil.userPermission() });
          queryClient.invalidateQueries({ queryKey: cacheKeysUtil.userPermissionTypes() });
        }, 1000); // Increased delay to 1 second to allow complete state settling
      },
    }
  );
};

/**
 * Hook to delete user permissions
 * @returns Mutation function for deleting user permissions
 */
export const useDeleteUserPermission = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id }: { id: string }) => {
      const response = await tFinancialService.api.deleteUserPermission(id);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate all users-related queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['users'] });
        queryClient.invalidateQueries({ queryKey: ['usersMetadata'] });

        // Also invalidate user details and permission queries in case the current user's permissions were affected
        queryClient.invalidateQueries({ queryKey: cacheKeysUtil.userDetails() });
        queryClient.invalidateQueries({ queryKey: cacheKeysUtil.userPermission() });
        queryClient.invalidateQueries({ queryKey: cacheKeysUtil.userPermissionTypes() });
      },
    }
  );
};
